/** @prettier */

import {Grid, Box, Stack, Tab} from '@mui/material';
import {useEffect, useState, ReactElement} from 'react';
import {useParams} from 'react-router-dom';
import {isUndefined} from 'lodash';
import {utcToZonedTime, format, zonedTimeToUtc} from 'date-fns-tz';
import {TabContext, TabPanel, TabList} from '@mui/lab';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';

//API import
import {
  getCompositionsRTUnit,
  getStatesSitesRt,
  getCompositionsSitesBySiteID,
  getSettingUser,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {graphProps} from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import {
  invertComma,
  initialUpper,
  roundOff,
  monthTranslation,
} from '../../functions/convertString';
import {useValueRef} from '../../functions/useValueRef';
import {iconBlack, iconWhite, iconRed} from '../../define';

/**
 * 熱源機詳細
 * @param props props
 * @returns JSX.Element
 */
const QtonDetail = (props: {
  /** グラフに描画するデータをセットする */
  setGraphParam: graphProps['setGraphParam'];
  /** 詳細画面のタブ切り替え変数 */
  tabType: 'diagram' | 'bar' | 'line' | 'barLineMixed';
  /** 詳細画面のタブ切り替え変数（set関数） */
  setTabType: React.Dispatch<
    React.SetStateAction<'line' | 'bar' | 'barLineMixed' | 'diagram'>
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //URLパラメータの取得
  const {siteID, targetID: unitID} = useParams();
  //gwIDの取得
  let gwID = '';
  if (unitID !== undefined) {
    const validateID = unitID.split('+'); //[0]：ユニットの種類、[1]：ゲートウェイID、[2]：rtID
    gwID = validateID[1]; //gwID
  }

  //prettier-ignore
  //Q-tonの子機の登録情報
  const [childUnitInfo, setChildUnitInfo] = useState<apiIF.resParameters.rtUnitInformations[]>([]);
  //prettier-ignore
  //Q-tonの状態値
  const [statesData, setStatesData] = useState<apiIF.resParameters.rtOUStates | null>(null);
  //prettier-ignore
  //RT機器の子機の状態値（3台まで登録可能）
  const [child1StatesData, setChild1StatesData] = useState<apiIF.resParameters.rtOUStates | null>(null);
  //prettier-ignore
  const [child2StatesData, setChild2StatesData] = useState<apiIF.resParameters.rtOUStates | null>(null);
  //prettier-ignore
  const [child3StatesData, setChild3StatesData] = useState<apiIF.resParameters.rtOUStates | null>(null);
  //現在時刻（状態値の定期更新用）
  const [nowTime, setNowTime] = useState(new Date());
  //タイムゾーン
  const [timeZone, setTimeZone] = useState('');
  //prettier-ignore
  //圧力単位
  const [pressureUnit, setPressureUnit] = useState<apiIF.res.getSettingUser['unit']['pressure']>('MPa');
  //API応答待ちフラグ
  const [apiWait, setApiWait] = useState(false);

  /** タンク画像一覧 */
  const qtonDiagramTankSrc: {[ratio: string]: string} = {
    '0': 'unitArea__diagram__background__tank--0',
    '1': 'unitArea__diagram__background__tank--10',
    '2': 'unitArea__diagram__background__tank--20',
    '3': 'unitArea__diagram__background__tank--30',
    '4': 'unitArea__diagram__background__tank--40',
    '5': 'unitArea__diagram__background__tank--50',
    '6': 'unitArea__diagram__background__tank--60',
    '7': 'unitArea__diagram__background__tank--70',
    '8': 'unitArea__diagram__background__tank--80',
    '9': 'unitArea__diagram__background__tank--90',
    '10': 'unitArea__diagram__background__tank--100',
  };

  /**グラフアイコンの切り替えテーブル */
  const graphIconColor: {
    [type in 'diagram' | 'bar' | 'line' | 'barLineMixed']: {
      barLineMixed: string;
      bar: string;
      line: string;
    };
  } = {
    diagram: {
      barLineMixed: iconBlack.BAR_GRAPH.src,
      bar: iconBlack.BAR_GRAPH.src,
      line: iconBlack.LINE_GRAPH.src,
    },
    barLineMixed: {
      barLineMixed: iconRed.BAR_GRAPH.src,
      bar: iconBlack.BAR_GRAPH.src,
      line: iconBlack.LINE_GRAPH.src,
    },
    bar: {
      barLineMixed: iconBlack.BAR_GRAPH.src,
      bar: iconRed.BAR_GRAPH.src,
      line: iconBlack.LINE_GRAPH.src,
    },
    line: {
      barLineMixed: iconBlack.BAR_GRAPH.src,
      bar: iconBlack.BAR_GRAPH.src,
      line: iconRed.LINE_GRAPH.src,
    },
  };

  /** 状態値取得用APIのクエリ */
  const statesAPIQuery: apiIF.reqParameters.getStatesSitesRt = {
    fields:
      'unit-name,run-stop,operation-mode,outlet-temperature,' +
      'error-code,target-outlet-temperature,target-hot-water-amount,' +
      'target-inlet-temperature,remote-control-preset-temperature,' +
      'demand,silent-mode,hot-water-amount,inlet-temperature,' +
      'outdoor-temperature,' +
      'compressor-run-stop,' +
      'tank-sensor-temperature,high-pressure,low-pressure,target-temperature-to-top-up',
  };
  /** 当日分の熱量COP取得用APIのクエリ */
  const energyAPIQuery: apiIF.reqParameters.getStatesSitesRt = {
    'fields': 'energy-amount,cop',
    'every-minute': 1440,
  };

  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID) || isUndefined(unitID)) return;

    setApiWait(true);
    setChild1StatesData(null);
    setChild2StatesData(null);
    setChild3StatesData(null);

    const promises = [];
    //選択したQ-tonを親機設定に登録している熱源機の情報を取得
    promises.push(getCompositionsRTUnit(siteID, gwID, {'max-result': 400}));
    //圧力単位を取得
    promises.push(getSettingUser());
    //サイトのタイムゾーンを取得
    promises.push(getCompositionsSitesBySiteID(siteID));
    /** Q-tonの子機の登録情報 */
    let qtonChildUnit: apiIF.resParameters.rtUnitInformations[] = [];

    Promise.all(promises)
      .then(info => {
        /** 熱源機の情報 */
        const rtInfo = info[0] as apiIF.res.getCompositionsRTUnit;
        /** ユーザの情報 */
        const userInfo = info[1] as apiIF.res.getSettingUser;
        /** サイトの情報 */
        const siteInfo = info[2] as apiIF.res.getCompositionsSitesBySiteID;

        if (rtInfo['unit-informations'].length > 0) {
          qtonChildUnit = rtInfo['unit-informations'].filter(data => {
            return data['parent-unit'] === unitID;
          });
          setChildUnitInfo(qtonChildUnit);
        }
        setPressureUnit(userInfo.unit.pressure);
        setTimeZone(siteInfo.timezone);

        //状態値を取得
        const statesPromises = [];
        //熱量とCOP以外の状態値を取得
        statesPromises.push(getStatesSitesRt(siteID, statesAPIQuery));
        //当日分の熱量とCOPを取得
        /** 状態値を取得する範囲の開始時刻 */
        const afterDate = utcToZonedTime(new Date(), siteInfo.timezone);
        afterDate.setHours(0, 0, 0, 0);
        statesPromises.push(
          getStatesSitesRt(siteID, {
            ...energyAPIQuery,
            'after-time': zonedTimeToUtc(
              afterDate,
              siteInfo.timezone
            ).toISOString(),
          })
        );
        return Promise.all(statesPromises);
      })
      .then(response => {
        /** 熱量とCOP以外の状態値 */
        const states = response[0]['rt-states'];
        /** 当日分の熱量とCOP */
        const energy = response[1]['rt-states'];

        //親機の状態値を検索
        const parentState = searchLatestState(unitID, states, energy);
        if (parentState !== null) {
          setStatesData(parentState);
        }
        //子機の状態値を検索
        if (qtonChildUnit.length > 0 && states.length > 0) {
          const searchChildStates =
            createFuncForSearchLatestChildStates(states);
          searchChildStates(setChild1StatesData, qtonChildUnit[0]['unit-id']);
          if (qtonChildUnit[1] !== undefined) {
            searchChildStates(setChild2StatesData, qtonChildUnit[1]['unit-id']);
          }
          if (qtonChildUnit[2] !== undefined) {
            searchChildStates(setChild3StatesData, qtonChildUnit[2]['unit-id']);
          }
        }
      })
      .finally(() => {
        setApiWait(false);
      });
  }, [unitID, siteID, gwID]);

  /** タイマ動作後にunitIDが変わった場合、1分後に参照するsiteIDはタイマ動作開始時のunitIDとなるため */
  /** useRefを使って、現在のunitIDを参照するようにしている。 */
  const nowUnit = useValueRef(unitID);
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID)) return;

    const intervalId = setInterval(() => {
      setNowTime(new Date());

      const statesPromises = [];
      //熱量とCOP以外の状態値を取得
      statesPromises.push(getStatesSitesRt(siteID, statesAPIQuery));
      //当日分の熱量とCOPを取得
      /** 状態値を取得する範囲の開始時刻 */
      const afterDate = utcToZonedTime(new Date(), timeZone);
      afterDate.setHours(0, 0, 0, 0);
      statesPromises.push(
        getStatesSitesRt(siteID, {
          ...energyAPIQuery,
          'after-time': zonedTimeToUtc(afterDate, timeZone).toISOString(),
        })
      );

      Promise.all(statesPromises).then(response => {
        /** 熱量とCOP以外の状態値 */
        const states = response[0]['rt-states'];
        /** 当日分の熱量とCOP */
        const energy = response[1]['rt-states'];

        //親機の状態値を検索
        if (nowUnit.current !== undefined) {
          const parentState = searchLatestState(
            nowUnit.current,
            states,
            energy
          );
          if (parentState !== null) {
            setStatesData(parentState);
          }
        }
        //子機の状態値を検索
        if (childUnitInfo.length > 0 && states.length > 0) {
          const searchChildStates =
            createFuncForSearchLatestChildStates(states);
          searchChildStates(setChild1StatesData, childUnitInfo[0]['unit-id']);
          if (childUnitInfo[1] !== undefined) {
            searchChildStates(setChild2StatesData, childUnitInfo[1]['unit-id']);
          }
          if (childUnitInfo[2] !== undefined) {
            searchChildStates(setChild3StatesData, childUnitInfo[2]['unit-id']);
          }
        }
      });
    }, 60000);

    return function () {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTime]);

  /**
   * 最新の状態値を検索
   * @param unitID 検索する熱源機のID
   * @param states 熱量とCOP以外の状態値
   * @param energy 当日分の熱量とCOP
   * @returns 最新の状態値
   */
  const searchLatestState = (
    unitID: string,
    states: apiIF.res.getStatesSitesRt['rt-states'],
    energy: apiIF.res.getStatesSitesRt['rt-states']
  ) => {
    if (states.length > 0 && energy.length > 0) {
      /** 熱量とCOP以外の状態値 */
      const unitState = states[0]['outdoor-unit-states'].find(data => {
        return data['unit-id'] === unitID;
      });
      /** 当日分の熱量とCOP */
      const unitEnergy = energy[0]['outdoor-unit-states'].find(data => {
        return data['unit-id'] === unitID;
      });
      if (unitState !== undefined && unitEnergy !== undefined) {
        return {...unitState, ...unitEnergy};
      }
    }
    return null;
  };

  /**
   * 子機の最新の状態値を検索するための関数を作成
   * @param states 最新の状態値
   * @returns 関数
   */
  const createFuncForSearchLatestChildStates = (
    states: apiIF.res.getStatesSitesRt['rt-states']
  ) => {
    return (
      setData: React.Dispatch<
        React.SetStateAction<apiIF.resParameters.rtOUStates | null>
      >,
      childrenUnitId: apiIF.resParameters.rtOUStates['unit-id']
    ) => {
      const state = states[0]['outdoor-unit-states'].find(data => {
        return data['unit-id'] === childrenUnitId;
      });
      if (state !== undefined) {
        setData(state);
      }
    };
  };

  /**
   * タブ変更時
   * @param event
   * @param newValue
   */
  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: 'bar' | 'line' | 'barLineMixed' | 'diagram',
    unitID: string,
    unitName: string | undefined,
    childUnitInfo: apiIF.resParameters.rtUnitInformations[]
  ) => {
    props.setTabType(newValue);
    if (newValue === 'diagram') {
      props.setGraphParam('');
    }
    if (newValue === 'barLineMixed' || newValue === 'bar') {
      props.setGraphParam('qton', unitID, newValue, unitName);
    }
    if (newValue === 'line') {
      props.setGraphParam(
        'qton',
        unitID,
        newValue,
        unitName,
        newValue,
        childUnitInfo
      );
    }
  };

  /**
   * エラーコードに基づいてエラー出力とアイコンを表示
   * @param errorCode - 受信したエラーコード
   * @returns JSX.Element
   */
  const ErrorDisplay = (errorCode: string | undefined) => {
    if (errorCode === undefined || errorCode === '00') {
      return '―';
    }

    if (errorCode === '100') {
      return `${trans('QtonDetail.item.value.error')}`; //通信異常時
    }

    if (errorCode === '') {
      // 空文字の場合は未課金状態のため何も表示しない
      return <></>;
    }

    // エラー表示
    return (
      <Grid
        container
        columnSpacing={1}
        justifyContent="center"
        alignItems="center">
        <Grid item xs="auto">
          <img
            src={iconBlack.ERROR.src}
            alt={`${trans(
              `QtonDetail.image.alternativeText.${iconBlack.ERROR.alt}`
            )}`}
            className="unitArea__stateList__state__value__icon--error"
          />
        </Grid>
        <Grid item xs="auto" id="mv-qtonDetailDataErrorCode">
          {`E${errorCode}`}
        </Grid>
      </Grid>
    );
  };

  /**
   * 運転/停止アイコン
   * @param props props
   * @returns JSX.Element
   */
  const RunStopIcon = (props: {
    /** 運転/停止 */
    runStop: apiIF.resParameters.rtOUStates['run-stop'];
  }) => {
    if (props.runStop === undefined) {
      return <></>;
    }

    /**
     * 運転/停止アイコン用クラス名
     * @param runStopState 現在の状態
     * @returns クラス名
     */
    const runStopIconClassName = (
      runStopState: apiIF.resParameters.rtOUStates['run-stop']
    ) => {
      switch (runStopState) {
        case 'run':
          return '';
        case 'stop':
          return '';
        case 'pause':
          return '';
        default:
          return 'unitArea__stateList__state__value__icon__runStopIcon--stop';
      }
    };
    /**
     * 運転/停止アイコン画像用クラス名
     * @param runStopState 現在の状態
     * @returns クラス名
     */
    const runStopIconImageClassName = (
      runStopState: apiIF.resParameters.rtOUStates['run-stop']
    ) => {
      switch (runStopState) {
        case 'run':
          return 'unitArea__stateList__state__value__icon__runStopIcon__image';
        case 'stop':
          return 'unitArea__stateList__state__value__icon__runStopIcon__image';
        case 'pause':
          return 'unitArea__stateList__state__value__icon__runStopIcon__image';
        default:
          return 'unitArea__stateList__state__value__icon__runStopIcon__smallImage';
      }
    };
    /**
     * 運転/停止用アイコン一覧
     * @param runStop 現在の状態
     * @returns アイコン
     */
    const runStopIconSource = (
      runStop: apiIF.resParameters.rtOUStates['run-stop']
    ) => {
      switch (runStop) {
        case 'run':
          return iconBlack.HOT_WATER_RUNNING.src;
        case 'stop':
          return iconBlack.HOT_WATER_STOPPED.src;
        case 'pause':
          return iconBlack.HOT_WATER_PAUSED.src;
        default:
          return iconBlack.MISSING.src;
      }
    };

    return (
      <Box className={runStopIconClassName(props.runStop)}>
        <img
          id="mv-qtonDetailDataRunStop"
          className={runStopIconImageClassName(props.runStop)}
          src={runStopIconSource(props.runStop)}
          title={`${trans(`QtonDetail.image.title.${props.runStop}`, {
            defaultValue: initialUpper(props.runStop as string), //undefinedでないことは確認しているため問題ない
          })}`}
          alt={`${trans(`QtonDetail.image.alternativeText.powerIcon`)}`}
        />
      </Box>
    );
  };

  /**
   * 運転モードアイコン
   * @param props props
   * @returns JSX.Element
   */
  const OperationModeIcon = (props: {
    /** 運転モード */
    operationMode: apiIF.resParameters.rtOUStates['operation-mode'];
  }) => {
    if (isView(props.operationMode) === false) {
      return (
        <Grid item xs="auto">
          --
        </Grid>
      );
    }

    /**
     * 運転モード変換テーブル
     * @param mode 現在の状態
     * @returns 変換した結果
     */
    const operationModeDisplayText = (
      mode: apiIF.resParameters.rtOUStates['operation-mode']
    ) => {
      switch (mode) {
        case 'none':
          return '--';
        case 'topUp':
          return trans('QtonDetail.item.value.operationMode.topUp');
        case 'keepWarm':
          return trans('QtonDetail.item.value.operationMode.keepWarm');
        case 'antiFreezing':
          return trans('QtonDetail.item.value.operationMode.antiFrost');
        case 'fillUp':
          return trans('QtonDetail.item.value.operationMode.fillUp');
        case 'defrost':
          return trans('QtonDetail.item.value.operationMode.defrost');
        case 'legionella':
          return trans('QtonDetail.item.value.operationMode.legionella');
        case 'heating':
          return trans('QtonDetail.item.value.operationMode.heating');
        default:
          return '';
      }
    };

    return (
      <>
        <Grid item xs="auto">
          <Box className="unitArea__stateList__state__value__icon--operationMode__border">
            <img
              id="mv-qtonDetailDataOperationModeImg"
              className="unitArea__stateList__state__value__icon--operationMode__border__image"
              src={iconWhite.HOT_WATER_ON.src}
              title={`${trans(`QtonDetail.image.title.${props.operationMode}`, {
                defaultValue: initialUpper(props.operationMode as string), //undefinedでないことは確認しているため問題ない
              })}`}
              alt={`${trans(
                `QtonDetail.image.alternativeText.${iconWhite.HOT_WATER_ON.alt}`
              )}`}
            />
          </Box>
        </Grid>
        <Grid
          item
          xs="auto"
          className="unitArea__stateList__state__value--mode">
          {operationModeDisplayText(props.operationMode)}
        </Grid>
      </>
    );
  };

  /** 現在の日付を翻訳して表示
   * @returns 現在日の日付表示
   */
  const displayNowDate = () => {
    /** 月名一覧 */
    const monthNameTable = [
      monthTranslation('january', trans),
      monthTranslation('february', trans),
      monthTranslation('march', trans),
      monthTranslation('april', trans),
      monthTranslation('may', trans),
      monthTranslation('june', trans),
      monthTranslation('july', trans),
      monthTranslation('august', trans),
      monthTranslation('september', trans),
      monthTranslation('october', trans),
      monthTranslation('november', trans),
      monthTranslation('december', trans),
    ];

    /** 現在の日付を取得 */
    const nowDate = utcToZonedTime(new Date(), timeZone);
    /** 現在日の日付をフォーマット化して日と年を取得 */
    const [day, , year] = format(nowDate, 'dd.MMMM yyyy').split(/[. ]/);
    /** 現在日の月の月名を取得 */
    const month = monthNameTable[nowDate.getMonth()];

    return `${day}.${month} ${year}`;
  };

  //Q-tonの状態値が無い場合は表示しない
  if (statesData === null) return <></>;
  return (
    <Stack spacing={2}>
      <Box className="unitArea__title">
        <Box className="unitArea__title__name" title={statesData['unit-name']}>
          {`${trans('QtonDetail.title')} ${statesData['unit-name']}`}
        </Box>
      </Box>
      <Box className="unitArea__stateList">
        <Grid container alignItems="flex-end" rowSpacing={2}>
          {/** 運転/停止 */}
          <StateContents
            title={trans('QtonDetail.item.title.power')}
            value={<RunStopIcon runStop={statesData['run-stop']} />}
          />
          {/** 運転モード　*/}
          <StateContents
            title={trans('QtonDetail.item.title.operationMode')}
            value={
              <Grid
                container
                columnSpacing={1}
                justifyContent="center"
                alignItems="center">
                <OperationModeIcon
                  operationMode={statesData['operation-mode']}
                />
              </Grid>
            }
          />
          {/** 室外温度 */}
          <StateContents
            title={trans('QtonDetail.item.title.outdoorTemp')}
            value={
              <Box>
                {isView(statesData['outdoor-temperature'])
                  ? invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(statesData['outdoor-temperature'] as string, 1)
                    )
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--degreeC">
                  &nbsp;{trans('QtonDetail.item.unit.temperatureUnit')}
                </Box>
              </Box>
            }
          />
          {/** 熱量　*/}
          <StateContents
            title={trans('QtonDetail.item.title.energyAmount')}
            value={
              <>
                {statesData['energy-amount'] !== undefined &&
                isView(statesData['energy-amount']['heating-mode'])
                  ? invertComma(
                      roundOff(
                        String(
                          Number(statesData['energy-amount']['heating-mode']) /
                            1000
                        ),
                        1
                      )
                    )
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--watt">
                  &nbsp;{trans('QtonDetail.item.unit.powerUnit')}
                </Box>
              </>
            }
          />
          {/** COP */}
          <StateContents
            title={trans('QtonDetail.item.title.cop')}
            value={
              isView(statesData.cop)
                ? //以下コンパイルエラーになってしまうため強制的に文字列にしている
                  //isView()で確認しているため問題ない
                  invertComma(roundOff(statesData.cop as string, 1))
                : '--'
            }
          />
          {/** エラーコード */}
          <StateContents
            title={trans('QtonDetail.item.title.error')}
            value={ErrorDisplay(statesData['error-code'])}
          />
          {/** 目標出湯温度 */}
          {/** target-temperature-to-top-upをTarget outlet temperatureと表示しているのは正しい仕様 */}
          <StateContents
            title={trans('QtonDetail.item.title.targetTemp')}
            value={
              <Box>
                {isView(statesData['target-temperature-to-top-up']) &&
                statesData['operation-mode'] !== 'heating'
                  ? invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(
                        statesData['target-temperature-to-top-up'] as string,
                        1
                      )
                    )
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--degreeC">
                  &nbsp;{trans('QtonDetail.item.unit.temperatureUnit')}
                </Box>
              </Box>
            }
          />
          {/** 目標貯湯量 */}
          <StateContents
            title={trans('QtonDetail.item.title.targetHotWaterAmount')}
            value={
              <Box>
                {isView(statesData['target-hot-water-amount']) &&
                statesData['operation-mode'] !== 'heating'
                  ? //以下コンパイルエラーになってしまうため強制的に文字列にしている
                    //isView()で確認しているため問題ない
                    roundOff(statesData['target-hot-water-amount'] as string)
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--degreeC">
                  &nbsp;{trans('QtonDetail.item.unit.percent')}
                </Box>
              </Box>
            }
          />
          {/** 目標入水温度 */}
          <StateContents
            title={trans('QtonDetail.item.title.targetInletTemp')}
            value={
              <Box>
                {isView(statesData['target-inlet-temperature']) &&
                statesData['operation-mode'] === 'heating'
                  ? invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(
                        statesData['target-inlet-temperature'] as string,
                        1
                      )
                    )
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--degreeC">
                  &nbsp;{trans('QtonDetail.item.unit.temperatureUnit')}
                </Box>
              </Box>
            }
          />
          {/** リモコン設定温度 */}
          <StateContents
            title={trans('QtonDetail.item.title.heatingSetTemp')}
            value={
              <Box>
                {isView(statesData['remote-control-preset-temperature']) &&
                statesData['operation-mode'] === 'heating'
                  ? invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(
                        statesData[
                          'remote-control-preset-temperature'
                        ] as string,
                        1
                      )
                    )
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--degreeC">
                  &nbsp;{trans('QtonDetail.item.unit.temperatureUnit')}
                </Box>
              </Box>
            }
          />
          {/** デマンド制御 */}
          <StateContents
            title={trans('QtonDetail.item.title.demand')}
            value={
              <Box>
                {isView(statesData['demand']) && statesData['demand'] === 'on'
                  ? trans('QtonDetail.item.value.active')
                  : '--'}
              </Box>
            }
          />
          {/** 静音モード制御 */}
          <StateContents
            title={trans('QtonDetail.item.title.silentMode')}
            value={
              <Box>
                {isView(statesData['silent-mode']) &&
                statesData['silent-mode'] === 'on'
                  ? trans('QtonDetail.item.value.active')
                  : '--'}
              </Box>
            }
          />
        </Grid>
      </Box>
      <TabContext value={props.tabType}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          rowSpacing={1}
          className="unitArea__tabs">
          <Grid item xs={10} xl={2} className="unitArea__tabs__date">
            {/* 現在の日付を月名を翻訳して表示 */}
            {displayNowDate()}
          </Grid>
          <Grid item xs={12} lg={11} xl={9}>
            <TabList
              variant="fullWidth"
              className="unitArea__tabs__tabList"
              onChange={(
                event: React.SyntheticEvent,
                newValue: 'bar' | 'line' | 'barLineMixed' | 'diagram'
              ) => {
                handleTabChange(
                  event,
                  newValue,
                  statesData['unit-id'],
                  statesData['unit-name'],
                  childUnitInfo
                );
              }}>
              <Tab
                label={trans('QtonDetail.item.tab.waterCircuit')}
                value="diagram"
                className="unitArea__tabs__tabList__tab"
                disabled={apiWait}
              />
              <Tab
                label={trans('QtonDetail.item.tab.energyAmount')}
                value="barLineMixed"
                disabled={apiWait || statesData['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
                icon={
                  <img
                    className="unitArea__tabs__tabList__icon"
                    src={graphIconColor[props.tabType].barLineMixed}
                    alt={`${trans(
                      'QtonDetail.image.alternativeText.barLineMixed'
                    )}`}
                  />
                }
                iconPosition="start"
                className="unitArea__tabs__tabList__tab"
              />
              <Tab
                label={trans('QtonDetail.item.tab.powerConsumption')}
                value="bar"
                disabled={apiWait || statesData['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
                icon={
                  <img
                    className="unitArea__tabs__tabList__icon"
                    src={graphIconColor[props.tabType].bar}
                    alt={`${trans(
                      'QtonDetail.image.alternativeText.barGraph'
                    )}`}
                  />
                }
                iconPosition="start"
                className="unitArea__tabs__tabList__tab"
              />
              <Tab
                label={trans('QtonDetail.item.tab.trendReporting')}
                value="line"
                disabled={apiWait || statesData['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
                icon={
                  <img
                    className="unitArea__tabs__tabList__icon"
                    src={graphIconColor[props.tabType].line}
                    alt={`${trans(
                      'QtonDetail.image.alternativeText.lineGraph'
                    )}`}
                  />
                }
                iconPosition="start"
                className="unitArea__tabs__tabList__tab"
              />
            </TabList>
          </Grid>
        </Grid>
        <TabPanel value="diagram">
          <Box className="unitArea__diagram">
            <Grid container justifyContent="center">
              <Grid item xs="auto" className="unitArea__diagram__background">
                {/** コンプレッサー */}
                {child1StatesData === null ? (
                  <NoChildComp
                    statesData={statesData}
                    pressureUnit={pressureUnit}
                    trans={trans}
                  />
                ) : (
                  <HasChildComp
                    statesData={statesData}
                    child1={child1StatesData}
                    child2={child2StatesData}
                    child3={child3StatesData}
                    pressureUnit={pressureUnit}
                    trans={trans}
                  />
                )}
                {/** 貯湯量タンク */}
                {statesData['operation-mode'] !== 'heating' && (
                  <Box
                    className={
                      isView(statesData['hot-water-amount'])
                        ? //prettier-ignore
                          qtonDiagramTankSrc[Math.floor(Number(statesData['hot-water-amount']) / 10)]
                        : 'unitArea__diagram__background__tank--0'
                    }>
                    <Grid
                      container
                      columnSpacing={5}
                      className="unitArea__diagram__background__tank__data">
                      {/** 出湯入湯温度 */}
                      <Grid item xs={6}>
                        <Stack
                          spacing={3}
                          className="unitArea__diagram__background__tank__data__inOutTemperature">
                          {/** 出湯温度 */}
                          <Box className="unitArea__diagram__background__tank__data__inOutTemperature__outlet__title--heating">
                            {trans('QtonDetail.diagram.value.outletTemp')}
                          </Box>
                          <Box className="unitArea__diagram__background__tank__data__inOutTemperature__outlet__number--qton">
                            {isView(statesData['outlet-temperature'])
                              ? invertComma(
                                  //以下コンパイルエラーになってしまうため強制的に文字列にしている
                                  //isView()で確認しているため問題ない
                                  roundOff(
                                    statesData['outlet-temperature'] as string,
                                    1
                                  )
                                )
                              : '--'}
                            &nbsp;
                            {trans('QtonDetail.diagram.unit.temperatureUnit')}
                          </Box>
                          {/** 入湯温度 */}
                          <Box className="unitArea__diagram__background__tank__data__inOutTemperature__inlet__number--qton">
                            {isView(statesData['inlet-temperature'])
                              ? invertComma(
                                  //以下コンパイルエラーになってしまうため強制的に文字列にしている
                                  //isView()で確認しているため問題ない
                                  roundOff(
                                    statesData['inlet-temperature'] as string,
                                    1
                                  )
                                )
                              : '--'}
                            &nbsp;
                            {trans('QtonDetail.diagram.unit.temperatureUnit')}
                          </Box>
                          <Box className="unitArea__diagram__background__tank__data__inOutTemperature__inlet__title--heating">
                            {trans('QtonDetail.diagram.value.inletTemp')}
                          </Box>
                        </Stack>
                      </Grid>
                      {/** 貯湯温度、貯湯量 */}
                      <Grid item xs={6}>
                        <Stack
                          justifyContent="space-between"
                          className="unitArea__diagram__background__tank__data__tankState">
                          {/** 貯湯温度 */}
                          <Box>
                            <Stack alignItems="center" spacing={1}>
                              <Box className="unitArea__diagram__background__tank__data__tankState__title">
                                {trans(
                                  'QtonDetail.diagram.value.tankSensorTemp'
                                )}
                              </Box>
                              <Box className="unitArea__diagram__background__tank__data__tankState__valueBox">
                                {isView(statesData['tank-sensor-temperature'])
                                  ? invertComma(
                                      //prettier-ignore
                                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                                      //isView()で確認しているため問題ない
                                      roundOff(statesData['tank-sensor-temperature'] as string, 1)
                                    )
                                  : '--'}
                                &nbsp;
                                {trans(
                                  'QtonDetail.diagram.unit.temperatureUnit'
                                )}
                              </Box>
                            </Stack>
                          </Box>
                          {/** 貯湯量 */}
                          <Box>
                            <Stack alignItems="center" spacing={1}>
                              <Box className="unitArea__diagram__background__tank__data__tankState__valueBox">
                                {isView(statesData['hot-water-amount'])
                                  ? //以下コンパイルエラーになってしまうため強制的に文字列にしている
                                    //isView()で確認しているため問題ない
                                    roundOff(
                                      statesData['hot-water-amount'] as string
                                    )
                                  : '--'}
                                &nbsp;
                                {trans('QtonDetail.diagram.unit.percent')}
                              </Box>
                              <Box className="unitArea__diagram__background__tank__data__tankState__title">
                                {trans(
                                  'QtonDetail.diagram.value.hotWaterAmount'
                                )}
                              </Box>
                            </Stack>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </TabContext>
    </Stack>
  );
};

/**
 * 状態値チェック
 * @param statesData 熱源機の状態値
 * @returns 判定結果
 */
const isView = (stateData: string | undefined) => {
  return stateData !== '' && stateData !== undefined;
};

/**
 * 状態値
 * @param props props
 * - title タイトル
 * - value 状態値
 * @returns JSX
 */
const StateContents = (props: {
  title: string;
  value: string | ReactElement;
}) => {
  return (
    <Grid item xs={4} lg={2} className="unitArea__stateList__state">
      <Stack spacing={3}>
        <Box className="unitArea__stateList__state__title">{props.title}</Box>
        <Box className="unitArea__stateList__state__value">
          <Box>{props.value}</Box>
        </Box>
      </Stack>
    </Grid>
  );
};

/**
 * 親機コンプレッサーの圧力
 * @param statesData 親機の状態値
 * @param pressureUnit 圧力単位
 * @param trans 翻訳関数
 * @returns JSX
 */
const ParentCompPressure = (props: {
  statesData: apiIF.resParameters.rtOUStates;
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  trans: TFunction;
}) => {
  /** 運転中かどうか */
  const isRun = props.statesData['compressor-run-stop'] === 'run';

  return (
    <Grid container justifyContent="center">
      <Grid item xs="auto">
        <Stack className="unitArea__diagram__background__comp__data__pressure">
          {/** タイトル */}
          <Box
            className={
              isRun
                ? 'unitArea__diagram__background__comp__data__pressure__title--run'
                : 'unitArea__diagram__background__comp__data__pressure__title--stop'
            }>
            {props.trans('QtonDetail.parentStat.compressor1')}
          </Box>
          {/** 高圧 */}
          <Grid
            container
            alignItems="center"
            columnSpacing={1}
            className={`unitArea__diagram__background__comp__data__pressure__box unitArea__diagram__background__comp__data__pressure__box--hp ${
              isRun
                ? ''
                : 'unitArea__diagram__background__comp__data__pressure__box--stop'
            }`}>
            <Grid item xs="auto">
              <Box
                className={
                  isRun
                    ? 'unitArea__diagram__background__comp__data__pressure__box__mark--run'
                    : 'unitArea__diagram__background__comp__data__pressure__box__mark--stop'
                }>
                {props.trans('QtonDetail.parentStat.highPressure')}
              </Box>
            </Grid>
            <Grid
              item
              xs
              className="unitArea__diagram__background__comp__data__pressure__box__number">
              {isView(props.statesData['high-pressure'])
                ? `${invertComma(
                    //以下コンパイルエラーになってしまうため強制的に文字列にしている
                    //isView()で確認しているため問題ない
                    roundOff(props.statesData['high-pressure'] as string, 1)
                  )}`
                : '--'}
              <Box
                component="span"
                className="unitArea__diagram__background__comp__data__pressure__box__number__unit">
                &nbsp;
                {props.pressureUnit === 'MPa'
                  ? props.trans('QtonDetail.diagram.unit.pressureUnit.mpa')
                  : props.trans('QtonDetail.diagram.unit.pressureUnit.bar')}
              </Box>
            </Grid>
          </Grid>
          {/** 低圧 */}
          <Grid
            container
            alignItems="center"
            columnSpacing={1}
            className={`unitArea__diagram__background__comp__data__pressure__box ${
              isRun
                ? ''
                : 'unitArea__diagram__background__comp__data__pressure__box--stop'
            }`}>
            <Grid item xs="auto">
              <Box
                className={
                  isRun
                    ? 'unitArea__diagram__background__comp__data__pressure__box__mark--run'
                    : 'unitArea__diagram__background__comp__data__pressure__box__mark--stop'
                }>
                {props.trans('QtonDetail.parentStat.lowPressure')}
              </Box>
            </Grid>
            <Grid
              item
              xs
              className="unitArea__diagram__background__comp__data__pressure__box__number">
              {isView(props.statesData['low-pressure'])
                ? `${invertComma(
                    //以下コンパイルエラーになってしまうため強制的に文字列にしている
                    //isView()で確認しているため問題ない
                    roundOff(props.statesData['low-pressure'] as string, 1)
                  )}`
                : '--'}
              <Box
                component="span"
                className="unitArea__diagram__background__comp__data__pressure__box__number__unit">
                &nbsp;
                {props.pressureUnit === 'MPa'
                  ? props.trans('QtonDetail.diagram.unit.pressureUnit.mpa')
                  : props.trans('QtonDetail.diagram.unit.pressureUnit.bar')}
              </Box>
            </Grid>
          </Grid>
        </Stack>
      </Grid>
    </Grid>
  );
};

/**
 * 子機コンプレッサーの圧力
 * @param statesData 各子機の状態値
 * @param pressureUnit 圧力単位
 * @param trans 翻訳関数
 * @returns JSX
 */
const ChildCompPressure = (props: {
  statesData: apiIF.resParameters.rtOUStates;
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  trans: TFunction;
}) => {
  const isRun = props.statesData['compressor-run-stop'] === 'run';

  return (
    <Stack spacing={1}>
      <Stack className="unitArea__diagram__background__comp__data__childrenComp__pressure">
        {/** タイトル */}
        <Box
          className={
            isRun
              ? 'unitArea__diagram__background__comp__data__childrenComp__pressure__title--on'
              : 'unitArea__diagram__background__comp__data__childrenComp__pressure__title--off'
          }>
          {`${props.trans('QtonDetail.childStat.compressor1')} (${
            props.pressureUnit === 'MPa'
              ? props.trans('QtonDetail.diagram.unit.pressureUnit.mpa')
              : props.trans('QtonDetail.diagram.unit.pressureUnit.bar')
          })`}
        </Box>
        {/** 高圧 */}
        <Grid
          container
          alignItems="center"
          columnSpacing={1}
          className={`unitArea__diagram__background__comp__data__childrenComp__pressure__box unitArea__diagram__background__comp__data__childrenComp__pressure__box--hp ${
            isRun
              ? ''
              : 'unitArea__diagram__background__comp__data__childrenComp__pressure__box--stop'
          }`}>
          <Grid item xs="auto">
            <Box
              className={
                isRun
                  ? 'unitArea__diagram__background__comp__data__childrenComp__pressure__box__mark--run'
                  : 'unitArea__diagram__background__comp__data__childrenComp__pressure__box__mark--stop'
              }>
              {props.trans('QtonDetail.childStat.highPressure')}
            </Box>
          </Grid>
          <Grid
            item
            xs
            className="unitArea__diagram__background__comp__data__childrenComp__pressure__box__number">
            {isView(props.statesData['high-pressure'])
              ? `${invertComma(
                  //以下コンパイルエラーになってしまうため強制的に文字列にしている
                  //isView()で確認しているため問題ない
                  roundOff(props.statesData['high-pressure'] as string, 1)
                )}`
              : '--'}
          </Grid>
        </Grid>
        {/** 低圧 */}
        <Grid
          container
          alignItems="center"
          columnSpacing={1}
          className={`unitArea__diagram__background__comp__data__childrenComp__pressure__box ${
            isRun
              ? ''
              : 'unitArea__diagram__background__comp__data__childrenComp__pressure__box--stop'
          }`}>
          <Grid item xs="auto">
            <Box
              className={
                isRun
                  ? 'unitArea__diagram__background__comp__data__childrenComp__pressure__box__mark--run'
                  : 'unitArea__diagram__background__comp__data__childrenComp__pressure__box__mark--stop'
              }>
              {props.trans('QtonDetail.parentStat.lowPressure')}
            </Box>
          </Grid>
          <Grid
            item
            xs
            className="unitArea__diagram__background__comp__data__childrenComp__pressure__box__number">
            {isView(props.statesData['low-pressure'])
              ? `${invertComma(
                  //以下コンパイルエラーになってしまうため強制的に文字列にしている
                  //isView()で確認しているため問題ない
                  roundOff(props.statesData['low-pressure'] as string, 1)
                )}`
              : '--'}
          </Grid>
        </Grid>
      </Stack>
      {/** 空調機名 */}
      <Box
        className="unitArea__diagram__background__comp__data__childrenComp__name"
        title={props.statesData['unit-name']}>
        {props.statesData['unit-name'] !== undefined ? (
          props.statesData['unit-name']
        ) : (
          <></>
        )}
      </Box>
    </Stack>
  );
};

/**
 * 子機接続が無い場合のコンプレッサー
 * @param statesData 熱源機の状態値
 * @param pressureUnit 圧力単位
 * @param trans 翻訳関数
 * @returns JSX
 */
const NoChildComp = (props: {
  statesData: apiIF.resParameters.rtOUStates;
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  trans: TFunction;
}) => {
  const isRun = props.statesData['compressor-run-stop'] === 'run';

  return (
    <Stack className="unitArea__diagram__background__comp--noParent">
      {/** 親機名 ※表示しないが以降のコンポネントの位置を調整するために必要 */}
      <Box className="unitArea__diagram__background__comp__data__parentName"></Box>
      {/** コンプレッサー */}
      <ParentCompPressure
        statesData={props.statesData}
        pressureUnit={props.pressureUnit}
        trans={props.trans}
      />
      {/** 子機コンプレッサー背景 */}
      <Grid
        container
        justifyContent="center"
        className="unitArea__diagram__background__comp__data__noChildCompBackground">
        <Grid
          item
          xs={4}
          className={
            isRun
              ? 'unitArea__diagram__background__comp__data__noChildCompBackground--run'
              : 'unitArea__diagram__background__comp__data__noChildCompBackground--stop'
          }></Grid>
      </Grid>
    </Stack>
  );
};

/**
 * 子機接続がある場合のコンプレッサー
 * @param statesData 親機の状態値
 * @param child1StatesData 子機1の状態値
 * @param child2StatesData 子機2の状態値
 * @param child3StatesData 子機2の状態値
 * @param pressureUnit 圧力単位
 * @param trans 翻訳関数
 * @returns JSX
 */
const HasChildComp = (props: {
  statesData: apiIF.resParameters.rtOUStates;
  child1: apiIF.resParameters.rtOUStates;
  child2: apiIF.resParameters.rtOUStates | null;
  child3: apiIF.resParameters.rtOUStates | null;
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  trans: TFunction;
}) => {
  const isRun = props.statesData['compressor-run-stop'] === 'run';

  return (
    <Stack spacing={1} className="unitArea__diagram__background__comp--parent">
      {/** 親機名 */}
      <Box
        className="unitArea__diagram__background__comp__data__parentName"
        title={props.statesData['unit-name']}>
        {props.statesData['unit-name']}
      </Box>
      {/** 親機コンプレッサー */}
      <ParentCompPressure
        statesData={props.statesData}
        pressureUnit={props.pressureUnit}
        trans={props.trans}
      />
      {/** 子機コンプレッサー背景 */}
      <Grid
        container
        justifyContent="center"
        className="unitArea__diagram__background__comp__data__childCompBackground">
        <Grid
          item
          xs={4}
          className={
            isRun
              ? 'unitArea__diagram__background__comp__data__childCompBackground--run'
              : 'unitArea__diagram__background__comp__data__childCompBackground--stop'
          }></Grid>
      </Grid>
      {/** 子機コンプレッサー */}
      <Grid
        container
        spacing={1}
        className="unitArea__diagram__background__comp__data__childrenComp">
        <Grid item xs={4}>
          <ChildCompPressure
            statesData={props.child1}
            pressureUnit={props.pressureUnit}
            trans={props.trans}
          />
        </Grid>
        <Grid item xs={4}>
          {props.child2 === null ? (
            <></>
          ) : (
            <ChildCompPressure
              statesData={props.child2}
              pressureUnit={props.pressureUnit}
              trans={props.trans}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {props.child3 === null ? (
            <></>
          ) : (
            <ChildCompPressure
              statesData={props.child3}
              pressureUnit={props.pressureUnit}
              trans={props.trans}
            />
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};

export default QtonDetail;
